/**
 * @author Sarah Nouh
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-07-19 02:00
 * @description Implementation of the panel navbar.
 * @filename panel-navbar.tsx
 */
import React from "react";
import { getCleanUuid } from "../../utilities/general";
import { SearchComponent } from "./search-component";
import { SVG } from "../../component-configuration/svgs";

/**
 * The props of the PanelNavbar control.
 *
 * @interface
 */
interface PanelNavbarProps {
  /**
   * flag to make not active tab disabled
   *
   * @type {boolean}
   */
  disableNotActive?: boolean;

  /**
   * The IDs of the equivalent tabs
   *
   * @type {Array<string>}
   */
  gridContent: string[];

  /**
   * The tab states (Index of active is "active" the rest are  empty strings"")
   *
   * @type {Array<string>}
   */
  tabState: string[];

  /**
   * The titles of the tabs
   *
   * @type {Array<string>}
   */
  tabText: any[];
  /**
   * The icon of the tabs
   *
   * @type {Array<string>}
   */
  icons?: any[];
  /**
   * A boolean flag which is true if the navbar has a search-bar.
   *
   * @type {boolean}
   */
  hasSearch: boolean;

  /**
   * Navigation text.
   *
   * @type {string}
   */
  navText?: React.ReactNode;

  /**
   * Search text.
   *
   * @type {string}
   */
  searchText?: string;

  /**
   * navbarClick.
   *
   * @type {any}
   */
  navbarClick?: any;

  /**
   * the function to handle change on search text.
   *
   * @type {Function}
   * @memberof PanelNavbarProps
   */
  onSearchTextChange?: Function;

  id?: string;
}

/**
 * The state of the Search component.
 *
 * @interface
 */
interface PanelNavbarState {
  /**
   * The id of the current active tab
   *
   * @type {string}
   */
  currentTab: string;
  /**
   * The sum of the width of all nav items
   *
   * @type {string}
   */
  navWidth: number;
  /**
   * A boolean to handle whether the search component is shown or not
   *
   * @type {string}
   */
  showSearchComponent: boolean;
}

/**
 *  A React component that renders a panel navbar.
 *
 * @export
 * @class PanelNavbar
 * @extends {React.Component<PanelNavbarProps>}
 */
export class PanelNavbar extends React.Component<
  PanelNavbarProps,
  PanelNavbarState
> {
  /**
   * selectedTabName
   *
   * @type {string}
   */
  selectedTabName: string;
  /**
   *
   *
   * @type {*}
   * @memberof PanelNavbar
   */
  navRef: any;
  /**
   * Constructor.
   *
   * @param {PanelNavbarProps} props The props of the component.
   */
  constructor(props: PanelNavbarProps) {
    super(props);
    this.state = {
      currentTab: props.gridContent[0],
      navWidth: 0,
      showSearchComponent: false,
    };
    this.selectedTabName = "";
    // if a different tab should be active not the first one
    // if (this.props.tabState !== undefined)
    for (let i = 0; i < this.props.tabState.length; i++) {
      if (this.props.tabState[i] === "active") {
        this.selectedTabName = this.props.tabText[i];
        break;
      }
    }
    if (this.selectedTabName === "") {
      this.selectedTabName = this.props.tabText[0];
    }
    this.navRef = React.createRef();
  }
  /**
   * A function invoked to check whether to display a tab or a dropdown list based on screen width
   *
   * @type {function()}
   */
  handleDisplayTabOrDropdown = () => {
    // get all items within nav
    const allItems: HTMLElement[] = this.navRef.current.querySelectorAll(
      ".nav-item"
    );
    let itemsWidth: number = 0;

    // calculate the sum of their width
    if (allItems.length !== 1) {
      for (let i = 0; i < allItems.length; i++) {
        itemsWidth += allItems[i].offsetWidth;
      }
    }
    this.setState({
      navWidth: itemsWidth,
    });
  };
  /**
   * A function invoked on tab click
   *
   * @type {function(event: any)}
   */
  onTabSelected = (event: any, tabsType: string, index?: number) => {
    // clear all active tab
    if (tabsType === "dropdown") {
      const allActiveTabs = this.navRef.current.querySelectorAll("a.active");
      for (let i = 0; i < allActiveTabs.length; i++) {
        allActiveTabs[i].className = allActiveTabs[i].className.replace(
          "active",
          ""
        );
      }
    }
    // acknowledge media modal which tab is clicked
    this.selectedTabName = this.props.tabText[index];
    this.setState(
      {
        currentTab: this.props.gridContent[index],
      },
      () => {
        if (
          this.props.navbarClick !== undefined &&
          this.props.navbarClick !== null
        ) {
          this.props.navbarClick(event, this.state.currentTab, index);
        }
      }
    );
  };

  /**
   * A function invoked on search click
   *
   * @type {function()}
   */
  handleSearchClick = () => {
    this.setState({
      showSearchComponent: true,
    });
    document.getElementById(this.props.id).focus();
  };

  /**
   * A function invoked to handle showing or hiding search component
   *
   * @type {function(state: string)}
   */
  handleCloseSearch = (state: string): void => {
    // if close button is clicked then close
    if (state === "close") {
      this.setState({
        showSearchComponent: false,
      });
    } else {
      // search btn is click then open search component
      this.setState({
        showSearchComponent: true,
      });
      document.getElementById(this.props.id).focus();
    }
  };

  onClickEventListener(event){
    const target: any = event.target;
    if (
      !target.closest(".search-form") &&
      !target.closest(".panel-search-bar")
    ) {
      this.setState({
        showSearchComponent: false,
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.onClickEventListener.bind(this));
  }

  componentDidMount() {
    this.handleDisplayTabOrDropdown();
    // if clicked outside the search component-> close it
    document.addEventListener("click", this.onClickEventListener.bind(this));
  }
  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    if (this.selectedTabName === "" || this.selectedTabName === undefined) {
      this.selectedTabName = this.props.tabText[0];
    }

    return (
      <ul
        className="nav nav-tabs panel-nav"
        id="myTab"
        role="tablist"
        ref={this.navRef}
      >
        {this.props.gridContent.map((item, index) => {
          return (
            <li
              className={`nav-item ${this.props.tabState[index]}`}
              key={index.toString()}
            >
              <a
                className={`nav-link text-uppercase ${this.props.tabState[
                  index
                ] +
                  (this.props.disableNotActive &&
                  this.props.tabState[index] === ""
                    ? " dimmed"
                    : "")}`}
                id={`${item}_id`}
                data-toggle={this.props.disableNotActive ? "" : "tab"}
                href={`#${item}`}
                role="tab"
                ria-selected="true"
                onClick={(event) => {
                  event.preventDefault();
                  if (!this.props.disableNotActive) {
                    this.onTabSelected(event, "list", index);
                  }
                }}
              >
                {this.props.icons && this.props.icons.length
                  ? this.props.icons[index]
                  : null}
                {this.props.tabText[index]}
              </a>
            </li>
          );
        })}
        {/* </div> */}
        {/* {this.props.hasSearch ? (
          <form
            className="form-inline search-container"
            onSubmit={event => {
              // needed to prevent enter key press from submitting form
              event.preventDefault();
            }}
          >
            <div
              className={
                this.state.showSearchComponent
                  ? "search-form search-form-shown"
                  : " search-form"
              }
            >
              <SearchComponent
                placeholder={`Search ${this.selectedTabName}...`}
                panelFilter={
                  this.props.searchText.includes("organizations") ||
                  this.props.searchText.includes("start")
                    ? "organization"
                    : this.props.searchText.includes("people")
                    ? "people"
                    : "resources"
                }
                searchTag={
                  this.selectedTabName ? this.selectedTabName.toLowerCase() : ""
                }
                toggleSearch={() => {
                  this.handleCloseSearch("close");
                }}
                searchComponentOpened={this.state.showSearchComponent}
                id={this.props.id}
              />
            </div>
            <div
              className="search-overley"
              onClick={() => this.handleCloseSearch("close")}
            />
            {this.state.showSearchComponent ? (
              <button
                className="btn close-btn"
                type="button"
                onClick={() => this.handleCloseSearch("close")}
              >
               {SVG.crosshairsThin}
              </button>
            ) : (
              <span
                className="panel-search-bar"
                onClick={() => document.getElementById(this.props.id).focus()}
              >
                <input
                  className="form-control"
                  type="search"
                  placeholder={`Search ${this.selectedTabName}...`}
                  aria-label="Search"
                  key={getCleanUuid()}
                  onClick={() => this.handleSearchClick()}
                  onFocus={() => document.getElementById(this.props.id).focus()}
                />
                <button
                  className="btn"
                  type="button"
                  onClick={() => this.handleCloseSearch("search")}
                >
                  {SVG.search}
                </button>
              </span>
            )}
          </form>
        ) : (
          <span className="resources">{this.props.navText}</span>
        )} */}
      </ul>
    );
  }
}
